import { defineStore } from 'pinia';
import type { Exercise } from '@ilteducation/exercise-service-types';
import { configHelper } from '@ilteducation/config';
import { ref } from 'vue';
import axios from 'axios';

export type ExerciseWithProgress = Exercise & { progress?: { completed: boolean; locked: boolean } };
const exerciseApi = configHelper.getApiUrl('exercises');
const exerciseTrackerApi = configHelper.getApiUrl('exercise-completion-tracker');
const sort = (a: ExerciseWithProgress, b: ExerciseWithProgress) => (a.sort ?? 0) - (b.sort ?? 0);

export const useExerciseStore = defineStore('exercise', () => {
  const exercises = ref<Record<string, ExerciseWithProgress[]>>({});
  // completed exercises for each resource
  const completedExercises = ref<Record<string, string[]>>({});

  function refreshProgressState(resourceId: string) {
    const resourceExercises = exercises.value[resourceId];
    if (resourceExercises === undefined) {
      return;
    }
    // We track progress for quizzes only
    const quizzes = resourceExercises.filter((e) => e.type === 'quiz');
    const quizIds = quizzes.map((e) => e.id);
    // filter out progress for quizzes that don't exist anymore
    const currentProgress = (completedExercises.value[resourceId] ?? []).filter((id) => quizIds.includes(id));
    const quizzesWithProgress = quizzes.map((quiz, i) => {
      const completed = currentProgress.includes(quiz.id);
      return {
        ...quiz,
        progress: {
          completed,
          // First quiz is always unlocked
          // Completed quizzes are always unlocked
          // If previous quiz is completed, unlock the next quiz
          locked: i !== 0 && !completed && !currentProgress.includes(quizzes[i - 1].id),
          // TODO: Add "in progress" if submission exists?
        },
      };
    });
    exercises.value[resourceId] = [...resourceExercises.filter((e) => e.type !== 'quiz'), ...quizzesWithProgress].sort(
      sort,
    );
  }

  function setProgress(resourceId: string, exerciseId: string) {
    const resourceExercises = exercises.value[resourceId];
    if (resourceExercises === undefined) {
      return;
    }
    if (completedExercises.value[resourceId] === undefined) {
      completedExercises.value[resourceId] = [];
    }
    completedExercises.value[resourceId] = [...new Set([...completedExercises.value[resourceId], exerciseId])];
    refreshProgressState(resourceId);
  }

  async function getExercisesByResource(resourceId: string) {
    if (exercises.value[resourceId] === undefined) {
      exercises.value[resourceId] = (
        await axios.get(`${exerciseApi}/?resourceId=${resourceId}`).then((response) => response.data.data as Exercise[])
      ).sort(sort);
    }
    return exercises.value[resourceId];
  }

  async function getProgressByResource(resourceId: string) {
    if (exercises.value[resourceId] === undefined) {
      await getExercisesByResource(resourceId);
    }
    if (completedExercises.value[resourceId] === undefined) {
      completedExercises.value[resourceId] = await axios
        .get(`${exerciseTrackerApi}/${resourceId}`)
        .then((response) => response.data);
      refreshProgressState(resourceId);
    }
    return completedExercises.value[resourceId];
  }

  async function getExercisesWithProgress(resourceId: string) {
    await getExercisesByResource(resourceId);
    await getProgressByResource(resourceId);
    return exercises.value[resourceId];
  }

  return {
    exercises,
    completedExercises,
    setProgress,
    getExercisesWithProgress,
  };
});
